/**
 * Connections Modal - Ad Platform Connection Management
 * 
 * Handles OAuth flows, connection status, account selection, and sync management.
 * API credentials are read from SERVER environment variables only (no UI entry).
 */
import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Card,
  CardContent,
} from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Link2,
  Link2Off,
  CheckCircle2,
  XCircle,
  RefreshCw,
  AlertTriangle,
  Settings2,
  Loader2,
  Building2,
  Search,
  AlertCircle,
  Clock,
  ExternalLink,
  Copy,
  Check,
  Info,
  Eye,
  EyeOff,
  Key,
  HelpCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

const API = process.env.REACT_APP_BACKEND_URL;

// Platform configurations with credential fields for multi-tenant support
const PLATFORMS = [
  {
    id: 'meta',
    name: 'Meta Ads',
    description: 'Facebook & Instagram Ads',
    icon: '📘',
    color: 'bg-blue-500',
    available: true,
    credentialFields: [
      { key: 'app_id', label: 'App ID', placeholder: 'Your Meta App ID', type: 'text', required: true },
      { key: 'app_secret', label: 'App Secret', placeholder: 'Your Meta App Secret', type: 'password', required: true },
    ],
    setupInfo: {
      title: 'Meta Ads Setup Requirements',
      requirements: [
        { name: 'App ID', description: 'Your Meta App ID', where: 'Meta for Developers Dashboard → Your App → Settings → Basic' },
        { name: 'App Secret', description: 'Your Meta App Secret', where: 'Meta for Developers Dashboard → Your App → Settings → Basic' },
      ],
      steps: [
        'Go to developers.facebook.com and log in',
        'Create a new app or select an existing one',
        'Choose "Business" as the app type',
        'Navigate to Settings → Basic to find your App ID and App Secret',
        'Under Products, add "Facebook Login" and configure OAuth settings',
        'Add your redirect URI (copy from the "OAuth Redirect URI" section above)',
        'Request the required permissions: ads_management, ads_read, business_management',
      ],
      docsUrl: 'https://developers.facebook.com/docs/marketing-apis/get-started',
      consoleUrl: 'https://developers.facebook.com/apps/',
    },
  },
  {
    id: 'google',
    name: 'Google Ads',
    description: 'Search, Display & YouTube',
    icon: '🔍',
    color: 'bg-red-500',
    available: true,
    credentialFields: [
      { key: 'client_id', label: 'OAuth Client ID', placeholder: 'xxx.apps.googleusercontent.com', type: 'text', required: true },
      { key: 'client_secret', label: 'OAuth Client Secret', placeholder: 'Your Client Secret', type: 'password', required: true },
      { key: 'developer_token', label: 'Developer Token', placeholder: '22-character token', type: 'password', required: true },
    ],
    setupInfo: {
      title: 'Google Ads Setup Requirements',
      requirements: [
        { name: 'OAuth Client ID', description: 'OAuth 2.0 Client ID', where: 'Google Cloud Console → APIs & Services → Credentials' },
        { name: 'OAuth Client Secret', description: 'OAuth 2.0 Client Secret', where: 'Google Cloud Console → APIs & Services → Credentials' },
        { name: 'Developer Token', description: 'Google Ads API Developer Token', where: 'Google Ads → Tools & Settings → API Center' },
      ],
      steps: [
        'Go to console.cloud.google.com and create/select a project',
        'Enable the Google Ads API in APIs & Services → Library',
        'Create OAuth 2.0 credentials (Web application type)',
        'Add authorized redirect URI (copy from the "OAuth Redirect URI" section above)',
        'Log into your Google Ads account at ads.google.com',
        'Navigate to Tools & Settings → Setup → API Center',
        'Apply for and copy your Developer Token (test or production)',
      ],
      docsUrl: 'https://developers.google.com/google-ads/api/docs/get-started/introduction',
      consoleUrl: 'https://console.cloud.google.com/apis/credentials',
      adsUrl: 'https://ads.google.com/aw/apicenter',
    },
  },
  {
    id: 'microsoft',
    name: 'Microsoft Ads',
    description: 'Bing Search & Audience',
    icon: '🪟',
    color: 'bg-cyan-500',
    available: false,
  },
  {
    id: 'linkedin',
    name: 'LinkedIn Ads',
    description: 'B2B Advertising',
    icon: '💼',
    color: 'bg-blue-700',
    available: false,
  },
  {
    id: 'tiktok',
    name: 'TikTok Ads',
    description: 'TikTok For Business',
    icon: '🎵',
    color: 'bg-gray-900',
    available: false,
  },
  {
    id: 'chatgpt',
    name: 'ChatGPT Ads',
    description: 'OpenAI Conversational Ads',
    icon: '🤖',
    color: 'bg-emerald-600',
    available: false,
  },
];

// Backfill options
const BACKFILL_OPTIONS = [
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
  { value: 180, label: 'Last 180 days (default)' },
  { value: 365, label: 'Last 365 days' },
];

// Status badge config
const STATUS_CONFIG = {
  connected: {
    label: 'Connected',
    icon: CheckCircle2,
    className: 'bg-green-500/10 text-green-600 border-green-500/20',
  },
  disconnected: {
    label: 'Not Connected',
    icon: Link2Off,
    className: 'bg-gray-500/10 text-gray-500 border-gray-500/20',
  },
  error: {
    label: 'Error',
    icon: XCircle,
    className: 'bg-red-500/10 text-red-600 border-red-500/20',
  },
  expired: {
    label: 'Expired',
    icon: AlertCircle,
    className: 'bg-orange-500/10 text-orange-600 border-orange-500/20',
  },
  syncing: {
    label: 'Syncing',
    icon: RefreshCw,
    className: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  },
  in_progress: {
    label: 'Syncing...',
    icon: RefreshCw,
    className: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  },
  failed: {
    label: 'Sync Failed',
    icon: XCircle,
    className: 'bg-red-500/10 text-red-600 border-red-500/20',
  },
};

const ConnectionsModal = ({ open, onOpenChange, connections = [], onRefresh }) => {
  const { token } = useAuth();
  const [connecting, setConnecting] = useState(null);
  const [syncing, setSyncing] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [accountSelectionOpen, setAccountSelectionOpen] = useState(false);
  const [discoveredAccounts, setDiscoveredAccounts] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [backfillDays, setBackfillDays] = useState(180);
  const [accountSearchTerm, setAccountSearchTerm] = useState('');
  const [platformConfig, setPlatformConfig] = useState({});
  
  // Setup info modal state
  const [setupInfoOpen, setSetupInfoOpen] = useState(false);
  const [setupInfoPlatform, setSetupInfoPlatform] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  
  // Help dialog state (separate from credentials entry)
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [helpPlatform, setHelpPlatform] = useState(null);
  
  // Credentials entry state
  const [credentials, setCredentials] = useState({});
  const [showSecrets, setShowSecrets] = useState({});
  const [savingCredentials, setSavingCredentials] = useState(false);
  const [hasCredentials, setHasCredentials] = useState({});
  const [customRedirectUri, setCustomRedirectUri] = useState('');

  // Open help dialog for a platform (read-only instructions)
  const openHelpDialog = (platformId) => {
    const platform = PLATFORMS.find(p => p.id === platformId);
    setHelpPlatform(platform);
    setHelpDialogOpen(true);
  };

  // Open setup info modal for a platform
  const openSetupInfo = async (platformId) => {
    const platform = PLATFORMS.find(p => p.id === platformId);
    setSetupInfoPlatform(platform);
    
    // Explicitly clear all credentials - prevents any cached/autofilled values
    setCredentials({});
    setShowSecrets({});
    setCustomRedirectUri('');
    
    // Small delay then re-initialize empty credentials to fight autofill
    setTimeout(() => {
      const initialCreds = {};
      platform?.credentialFields?.forEach(field => {
        initialCreds[field.key] = '';
      });
      setCredentials(initialCreds);
    }, 100);
    
    // Fetch the server's configured redirect URI
    try {
      const response = await axios.get(
        `${API}/api/marketing/ads/connections/${platformId}/redirect-uri`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.redirect_uri) {
        setCustomRedirectUri(response.data.redirect_uri);
      }
    } catch (err) {
      // If endpoint doesn't exist, we'll use the frontend URL
      console.log('Using frontend URL for redirect URI');
    }
    
    setSetupInfoOpen(true);
  };

  // Toggle password visibility
  const toggleSecret = (fieldKey) => {
    setShowSecrets(prev => ({ ...prev, [fieldKey]: !prev[fieldKey] }));
  };

  // Check if platform has credentials stored
  const checkCredentials = async (platformId) => {
    try {
      const response = await axios.get(
        `${API}/api/marketing/ads/connections/${platformId}/has-credentials`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHasCredentials(prev => ({ ...prev, [platformId]: response.data.has_credentials }));
      return response.data.has_credentials;
    } catch (err) {
      return false;
    }
  };

  // Save credentials and initiate connection
  const handleSaveCredentials = async () => {
    if (!setupInfoPlatform) return;
    
    // Validate all required fields are filled
    const missingFields = setupInfoPlatform.credentialFields?.filter(
      field => field.required && !credentials[field.key]?.trim()
    );
    
    if (missingFields?.length > 0) {
      toast.error(`Please fill in: ${missingFields.map(f => f.label).join(', ')}`);
      return;
    }
    
    setSavingCredentials(true);
    
    try {
      // Save credentials to backend
      const response = await axios.post(
        `${API}/api/marketing/ads/connections/${setupInfoPlatform.id}/credentials`,
        credentials,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Credentials saved! Starting connection...');
        setSetupInfoOpen(false);
        setHasCredentials(prev => ({ ...prev, [setupInfoPlatform.id]: true }));
        
        // Now trigger the OAuth connection
        await handleConnect(setupInfoPlatform.id);
      }
    } catch (err) {
      console.error('Error saving credentials:', err);
      toast.error(err.response?.data?.detail || 'Failed to save credentials');
    } finally {
      setSavingCredentials(false);
    }
  };

  // Copy text to clipboard
  const copyToClipboard = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
      toast.success('Copied to clipboard');
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  // Get base URL for OAuth redirect URIs
  const getBaseUrl = () => {
    return process.env.REACT_APP_BACKEND_URL || window.location.origin;
  };

  // Get connection status for a platform
  const getConnectionStatus = (platformId) => {
    const conn = connections.find(c => c.platform === platformId);
    if (!conn) return 'disconnected';
    
    if (conn.last_sync_status === 'in_progress') return 'in_progress';
    if (conn.last_sync_status === 'failed') return 'failed';
    
    return conn.status || 'disconnected';
  };

  // Get connection details for a platform
  const getConnectionDetails = (platformId) => {
    return connections.find(c => c.platform === platformId);
  };

  // Format last sync time
  const formatLastSync = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  // Check if platform is configured (env vars set on server)
  const checkPlatformConfig = async (platformId) => {
    try {
      const response = await axios.get(
        `${API}/api/marketing/ads/connections/${platformId}/check-config`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPlatformConfig(prev => ({ ...prev, [platformId]: response.data }));
      return response.data;
    } catch (err) {
      console.error('Config check error:', err);
      return { configured: false, missing_vars: [] };
    }
  };

  // Handle connect button - shows setup info if not configured, or initiates OAuth
  const handleConnect = async (platformId) => {
    setConnecting(platformId);
    
    try {
      const response = await axios.post(
        `${API}/api/marketing/ads/connections/${platformId}/connect`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.oauth_url) {
        // Real OAuth flow - open popup for auth
        const width = 600;
        const height = 700;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;
        
        const popup = window.open(
          response.data.oauth_url,
          'oauth_popup',
          `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
        );
        
        // Poll for completion
        const pollInterval = setInterval(() => {
          try {
            if (popup.closed) {
              clearInterval(pollInterval);
              setConnecting(null);
              onRefresh?.();
              // After OAuth complete, show account selection
              setTimeout(() => discoverAccounts(platformId), 1000);
            }
          } catch (e) {
            // Cross-origin error means popup is on different domain
          }
        }, 500);
        
        toast.info('Complete the authorization in the popup window');
      } else if (response.data.demo_mode) {
        toast.success(`Connected to ${PLATFORMS.find(p => p.id === platformId)?.name} (Demo Mode)`);
        onRefresh?.();
      }
    } catch (err) {
      console.error('Connection error:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to initiate connection';
      
      // Show setup info modal when env vars are missing
      if (errorMsg.includes('environment variables') || errorMsg.includes('not configured')) {
        openSetupInfo(platformId);
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setConnecting(null);
    }
  };

  // Discover accounts after connection
  const discoverAccounts = async (platformId) => {
    setSelectedPlatform(platformId);
    setLoadingAccounts(true);
    setDiscoveredAccounts([]);
    setSelectedAccounts([]);
    
    try {
      const response = await axios.get(
        `${API}/api/marketing/ads/connections/${platformId}/discover-accounts`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setDiscoveredAccounts(response.data.accounts || []);
      setAccountSelectionOpen(true);
    } catch (err) {
      console.error('Account discovery error:', err);
      toast.error('Failed to discover accounts');
    } finally {
      setLoadingAccounts(false);
    }
  };

  // Handle account selection submit
  const handleAccountSelection = async () => {
    if (selectedAccounts.length === 0) {
      toast.error('Please select at least one account');
      return;
    }
    
    try {
      await axios.post(
        `${API}/api/marketing/ads/connections/${selectedPlatform}/select-accounts?${selectedAccounts.map(id => `account_ids=${id}`).join('&')}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Accounts selected. Starting initial sync...');
      setAccountSelectionOpen(false);
      
      await handleSync(selectedPlatform);
      
      onRefresh?.();
    } catch (err) {
      console.error('Account selection error:', err);
      toast.error('Failed to save account selection');
    }
  };

  // Handle disconnect
  const handleDisconnect = async (platformId) => {
    if (!confirm('Are you sure you want to disconnect this platform?')) return;
    
    try {
      await axios.post(
        `${API}/api/marketing/ads/connections/${platformId}/disconnect`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Platform disconnected');
      onRefresh?.();
    } catch (err) {
      console.error('Disconnect error:', err);
      toast.error('Failed to disconnect platform');
    }
  };

  // Handle manual sync
  const handleSync = async (platformId) => {
    setSyncing(platformId);
    
    try {
      const response = await axios.post(
        `${API}/api/marketing/ads/connections/${platformId}/sync?backfill_days=${backfillDays}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const stats = response.data.stats || {};
        toast.success(
          `Sync complete: ${stats.campaigns_synced || 0} campaigns, ${stats.insights_synced || 0} metrics synced`
        );
      } else {
        toast.error(response.data.error || 'Sync failed');
      }
      
      onRefresh?.();
    } catch (err) {
      console.error('Sync error:', err);
      toast.error(err.response?.data?.detail || 'Failed to sync');
    } finally {
      setSyncing(null);
    }
  };

  // Toggle account selection
  const toggleAccount = (accountId) => {
    setSelectedAccounts(prev => 
      prev.includes(accountId)
        ? prev.filter(id => id !== accountId)
        : [...prev, accountId]
    );
  };

  // Select all accounts
  const selectAllAccounts = () => {
    const filtered = filteredAccounts.map(a => a.account_id);
    setSelectedAccounts(filtered);
  };

  // Filter accounts by search
  const filteredAccounts = discoveredAccounts.filter(acc => 
    !accountSearchTerm || 
    acc.name?.toLowerCase().includes(accountSearchTerm.toLowerCase()) ||
    acc.account_id?.toLowerCase().includes(accountSearchTerm.toLowerCase())
  );

  return (
    <>
      {/* Main Connections Dialog */}
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings2 className="h-5 w-5" />
              Ad Platform Connections
            </DialogTitle>
            <DialogDescription>
              Connect your ad accounts to sync campaign data automatically.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-4">
            {PLATFORMS.map((platform) => {
              const status = getConnectionStatus(platform.id);
              const details = getConnectionDetails(platform.id);
              const statusConfig = STATUS_CONFIG[status] || STATUS_CONFIG.disconnected;
              const StatusIcon = statusConfig.icon;
              const isConnecting = connecting === platform.id;
              const isSyncing = syncing === platform.id;
              const isDemoMode = details?.demo_mode;

              return (
                <Card 
                  key={platform.id} 
                  className={`transition-all ${!platform.available ? 'opacity-60' : ''}`}
                  data-testid={`connection-${platform.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3">
                        {/* Platform Icon */}
                        <div className={`w-10 h-10 rounded-lg ${platform.color} flex items-center justify-center text-xl`}>
                          {platform.icon}
                        </div>
                        
                        {/* Platform Info */}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-medium">{platform.name}</h3>
                            <Badge 
                              variant="outline" 
                              className={`text-[10px] px-1.5 py-0 ${statusConfig.className}`}
                            >
                              <StatusIcon className={`h-3 w-3 mr-1 ${status === 'in_progress' ? 'animate-spin' : ''}`} />
                              {statusConfig.label}
                            </Badge>
                            {isDemoMode && (
                              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                                Demo Mode
                              </Badge>
                            )}
                            {!platform.available && (
                              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                                Coming Soon
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {platform.description}
                          </p>
                          
                          {/* Connection Details */}
                          {status === 'connected' && details && (
                            <div className="mt-2 text-xs text-muted-foreground space-y-1">
                              {details.account_name && (
                                <div className="flex items-center gap-1">
                                  <Building2 className="h-3 w-3" />
                                  <span>{details.account_name}</span>
                                </div>
                              )}
                              {details.selected_account_ids?.length > 0 && (
                                <div className="flex items-center gap-1">
                                  <span className="font-medium">Accounts:</span>
                                  <span>{details.selected_account_ids.length} selected</span>
                                </div>
                              )}
                              <div className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                <span>Last sync: {formatLastSync(details.last_sync_at)}</span>
                              </div>
                              {details.last_error && (
                                <div className="flex items-center gap-1 text-red-500">
                                  <AlertCircle className="h-3 w-3" />
                                  <span className="truncate max-w-[200px]" title={details.last_error}>
                                    {details.last_error}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex items-center gap-2 flex-wrap justify-end">
                        {platform.available && (
                          <>
                            {status === 'connected' ? (
                              <>
                                {/* Help Button */}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openHelpDialog(platform.id)}
                                  className="text-xs h-8"
                                  data-testid={`help-${platform.id}`}
                                  title="View setup instructions"
                                >
                                  <HelpCircle className="h-3 w-3" />
                                </Button>
                                
                                {/* Account Selection */}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => discoverAccounts(platform.id)}
                                  disabled={loadingAccounts}
                                  className="text-xs h-8"
                                  data-testid={`accounts-${platform.id}`}
                                >
                                  <Building2 className="h-3 w-3 mr-1" />
                                  Accounts
                                </Button>
                                
                                {/* Sync Button */}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleSync(platform.id)}
                                  disabled={isSyncing}
                                  className="text-xs h-8"
                                  data-testid={`sync-${platform.id}`}
                                >
                                  {isSyncing ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <RefreshCw className="h-3 w-3" />
                                  )}
                                  <span className="ml-1">Sync</span>
                                </Button>
                                
                                {/* Disconnect Button */}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDisconnect(platform.id)}
                                  className="text-xs h-8 text-red-600 hover:text-red-700"
                                  data-testid={`disconnect-${platform.id}`}
                                >
                                  <Link2Off className="h-3 w-3 mr-1" />
                                  Disconnect
                                </Button>
                              </>
                            ) : (
                              <>
                                {/* Help Button */}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openHelpDialog(platform.id)}
                                  className="text-xs h-8"
                                  data-testid={`help-${platform.id}`}
                                  title="View setup instructions"
                                >
                                  <HelpCircle className="h-3 w-3" />
                                </Button>
                                
                                {/* Connect Button */}
                                <Button
                                  size="sm"
                                  onClick={() => handleConnect(platform.id)}
                                  disabled={isConnecting}
                                  className="text-xs h-8"
                                  data-testid={`connect-${platform.id}`}
                                >
                                  {isConnecting ? (
                                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                  ) : (
                                    <Link2 className="h-3 w-3 mr-1" />
                                  )}
                                  Connect
                                </Button>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Backfill Settings */}
          <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
            <label className="text-xs font-medium">Default Backfill:</label>
            <Select value={backfillDays.toString()} onValueChange={(v) => setBackfillDays(Number(v))}>
              <SelectTrigger className="w-[180px] h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BACKFILL_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value.toString()} className="text-xs">
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Footer Info */}
          <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg text-xs text-muted-foreground">
            <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <div>
              Click &quot;Connect&quot; to enter your API credentials and authorize access. 
              Your credentials are encrypted and stored securely. Data syncs automatically after connection.
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Account Selection Dialog */}
      <Dialog open={accountSelectionOpen} onOpenChange={setAccountSelectionOpen}>
        <DialogContent className="max-w-lg max-h-[70vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Select Ad Accounts
            </DialogTitle>
            <DialogDescription>
              Choose which accounts to sync for {PLATFORMS.find(p => p.id === selectedPlatform)?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Search */}
            <div className="relative mb-3">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search accounts..."
                value={accountSearchTerm}
                onChange={(e) => setAccountSearchTerm(e.target.value)}
                className="pl-8 h-9 text-sm"
              />
            </div>

            {/* Account List */}
            <div className="flex-1 overflow-y-auto space-y-2 pr-2">
              {loadingAccounts ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : filteredAccounts.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                  <Building2 className="h-8 w-8 mb-2 opacity-50" />
                  <span className="text-sm">No accounts found</span>
                </div>
              ) : (
                filteredAccounts.map((account) => (
                  <div 
                    key={account.account_id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedAccounts.includes(account.account_id) 
                        ? 'bg-primary/5 border-primary/30' 
                        : 'hover:bg-muted/50'
                    }`}
                    onClick={() => toggleAccount(account.account_id)}
                  >
                    <Checkbox 
                      checked={selectedAccounts.includes(account.account_id)}
                      onCheckedChange={() => toggleAccount(account.account_id)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{account.name}</div>
                      <div className="text-xs text-muted-foreground">
                        ID: {account.account_id}
                        {account.currency && ` • ${account.currency}`}
                        {account.is_manager && ' • Manager Account'}
                      </div>
                    </div>
                    {account.status === 'active' && (
                      <Badge variant="outline" className="text-[10px] bg-green-500/10 text-green-600">
                        Active
                      </Badge>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Selection summary */}
            {filteredAccounts.length > 0 && (
              <div className="flex items-center justify-between pt-3 border-t mt-3">
                <Button variant="ghost" size="sm" onClick={selectAllAccounts} className="text-xs">
                  Select All ({filteredAccounts.length})
                </Button>
                <span className="text-xs text-muted-foreground">
                  {selectedAccounts.length} selected
                </span>
              </div>
            )}
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setAccountSelectionOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAccountSelection}
              disabled={selectedAccounts.length === 0}
            >
              Save & Sync ({selectedAccounts.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Setup Info & Credentials Dialog */}
      <Dialog open={setupInfoOpen} onOpenChange={setSetupInfoOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {setupInfoPlatform && (
                <>
                  <span className={`w-8 h-8 rounded-lg ${setupInfoPlatform.color} flex items-center justify-center text-lg`}>
                    {setupInfoPlatform.icon}
                  </span>
                  Connect {setupInfoPlatform.name}
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              Enter your API credentials to connect this platform. Your credentials are encrypted and stored securely.
            </DialogDescription>
          </DialogHeader>

          {setupInfoPlatform && (
            <div className="flex-1 overflow-y-auto space-y-6 pr-2">
              {/* Credentials Entry Section */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm flex items-center gap-2">
                  <Key className="h-4 w-4" />
                  API Credentials
                </h3>
                
                {setupInfoPlatform.credentialFields?.map((field, idx) => (
                  <div key={field.key} className="space-y-2">
                    <Label htmlFor={`api_cred_${setupInfoPlatform.id}_${field.key}_${idx}`} className="text-sm font-medium">
                      {field.label} {field.required && <span className="text-red-500">*</span>}
                    </Label>
                    <div className="relative">
                      <input
                        id={`api_cred_${setupInfoPlatform.id}_${field.key}_${idx}`}
                        name={`api_cred_${setupInfoPlatform.id}_${field.key}_${idx}`}
                        type={field.type === 'password' && !showSecrets[field.key] ? 'password' : 'text'}
                        placeholder={field.placeholder}
                        value={credentials[field.key] || ''}
                        onChange={(e) => setCredentials(prev => ({ ...prev, [field.key]: e.target.value }))}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 pr-10"
                        data-testid={`credential-${field.key}`}
                        autoComplete="one-time-code"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck="false"
                        data-lpignore="true"
                        data-1p-ignore="true"
                        data-form-type="other"
                      />
                      {field.type === 'password' && (
                        <button
                          type="button"
                          onClick={() => toggleSecret(field.key)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showSecrets[field.key] ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      )}
                    </div>
                    {/* Show where to find this credential */}
                    {setupInfoPlatform.setupInfo?.requirements?.find(r => r.name === field.label) && (
                      <p className="text-xs text-muted-foreground">
                        <span className="font-medium">Where to find: </span>
                        {setupInfoPlatform.setupInfo.requirements.find(r => r.name === field.label)?.where}
                      </p>
                    )}
                  </div>
                ))}
              </div>

              {/* OAuth Redirect URI - Auto-detect Environment */}
              <div className="space-y-3">
                <h3 className="font-semibold text-sm flex items-center gap-2">
                  <Link2 className="h-4 w-4" />
                  OAuth Redirect URI
                </h3>
                <div className="p-3 bg-blue-500/5 border border-blue-500/20 rounded-lg space-y-3">
                  <p className="text-xs text-muted-foreground">
                    Add this <strong>exact URL</strong> to your {setupInfoPlatform?.name} app&apos;s authorized redirect URIs.
                  </p>
                  
                  {/* Auto-detected environment and URI */}
                  {(() => {
                    const currentUrl = customRedirectUri || `${getBaseUrl()}/api/marketing/ads/oauth/callback/${setupInfoPlatform?.id}`;
                    const isPreview = currentUrl.includes('.preview.') || currentUrl.includes('localhost');
                    const envType = isPreview ? 'Preview' : 'Production';
                    const envColor = isPreview ? 'text-amber-600' : 'text-green-600';
                    const envBgColor = isPreview ? 'bg-amber-500/10 border-amber-500/30' : 'bg-green-500/10 border-green-500/30';
                    
                    return (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-medium ${envColor} flex items-center gap-1`}>
                            <span className={`w-2 h-2 rounded-full ${isPreview ? 'bg-amber-500' : 'bg-green-500'}`}></span>
                            Detected: {envType} Environment
                          </span>
                        </div>
                        
                        <div className={`p-3 rounded-lg border ${envBgColor}`}>
                          <div className="flex items-center gap-2">
                            <code className="flex-1 text-xs font-mono bg-background/50 p-2 rounded border break-all">
                              {currentUrl}
                            </code>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 px-3 flex-shrink-0"
                              onClick={() => copyToClipboard(currentUrl, 'redirect_uri')}
                            >
                              {copiedField === 'redirect_uri' ? (
                                <>
                                  <Check className="h-3 w-3 text-green-500 mr-1" />
                                  <span className="text-xs">Copied!</span>
                                </>
                              ) : (
                                <>
                                  <Copy className="h-3 w-3 mr-1" />
                                  <span className="text-xs">Copy</span>
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                        
                        {isPreview && (
                          <p className="text-xs text-muted-foreground">
                            💡 <strong>Tip:</strong> After deployment, re-open this modal to get your production URL, then add it to your {setupInfoPlatform?.name} app settings.
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Setup Guide - Collapsible */}
              <details className="group">
                <summary className="cursor-pointer font-semibold text-sm flex items-center gap-2 list-none">
                  <Info className="h-4 w-4" />
                  Setup Guide
                  <span className="text-xs text-muted-foreground ml-auto">(click to expand)</span>
                </summary>
                <div className="mt-3 space-y-3">
                  <ol className="space-y-2">
                    {setupInfoPlatform.setupInfo?.steps?.map((step, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-sm">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-semibold">
                          {idx + 1}
                        </span>
                        <span className="text-muted-foreground pt-0.5">
                          {step.replace('{baseUrl}', getBaseUrl())}
                        </span>
                      </li>
                    ))}
                  </ol>
                  
                  {/* Quick Links */}
                  <div className="flex flex-wrap gap-2 pt-2">
                    {setupInfoPlatform.setupInfo?.consoleUrl && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(setupInfoPlatform.setupInfo.consoleUrl, '_blank')}
                      >
                        <ExternalLink className="h-3 w-3 mr-2" />
                        Developer Console
                      </Button>
                    )}
                    {setupInfoPlatform.setupInfo?.adsUrl && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(setupInfoPlatform.setupInfo.adsUrl, '_blank')}
                      >
                        <ExternalLink className="h-3 w-3 mr-2" />
                        Ads API Center
                      </Button>
                    )}
                    {setupInfoPlatform.setupInfo?.docsUrl && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(setupInfoPlatform.setupInfo.docsUrl, '_blank')}
                      >
                        <ExternalLink className="h-3 w-3 mr-2" />
                        Documentation
                      </Button>
                    )}
                  </div>
                </div>
              </details>

              {/* Security Note */}
              <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="text-xs">
                    <p className="font-medium text-green-700 dark:text-green-500">Secure Storage</p>
                    <p className="text-muted-foreground mt-1">
                      Your credentials are encrypted before being stored. Secrets are never logged or exposed in the UI after saving.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="mt-4 border-t pt-4">
            <Button variant="outline" onClick={() => setSetupInfoOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveCredentials}
              disabled={savingCredentials}
              data-testid="save-credentials-btn"
            >
              {savingCredentials ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Key className="h-4 w-4 mr-2" />
              )}
              Save & Connect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Help Dialog - Read-only Setup Instructions */}
      <Dialog open={helpDialogOpen} onOpenChange={setHelpDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {helpPlatform && (
                <>
                  <HelpCircle className="h-5 w-5 text-blue-500" />
                  {helpPlatform.name} Setup Guide
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              Follow these steps to configure your {helpPlatform?.name} connection.
            </DialogDescription>
          </DialogHeader>

          {helpPlatform?.setupInfo && (
            <div className="flex-1 overflow-y-auto space-y-6 pr-2">
              {/* What You'll Need Section */}
              <div className="space-y-3">
                <h3 className="font-semibold text-sm flex items-center gap-2">
                  <Key className="h-4 w-4" />
                  What You&apos;ll Need
                </h3>
                <div className="space-y-2">
                  {helpPlatform.setupInfo.requirements?.map((req, idx) => (
                    <div key={idx} className="p-3 bg-muted/50 rounded-lg border">
                      <div className="font-medium text-sm">{req.name}</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        <span className="font-medium">Where to find: </span>
                        {req.where}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* OAuth Redirect URI Section */}
              <div className="space-y-3">
                <h3 className="font-semibold text-sm flex items-center gap-2">
                  <Link2 className="h-4 w-4" />
                  OAuth Redirect URI
                </h3>
                <div className="p-3 bg-blue-500/5 border border-blue-500/20 rounded-lg space-y-3">
                  <p className="text-xs text-muted-foreground">
                    Add this <strong>exact URL</strong> to your {helpPlatform?.name} app&apos;s authorized redirect URIs:
                  </p>
                  {(() => {
                    const redirectUri = `${getBaseUrl()}/api/marketing/ads/oauth/callback/${helpPlatform?.id}`;
                    return (
                      <div className="flex items-center gap-2">
                        <code className="flex-1 text-xs font-mono bg-background/50 p-2 rounded border break-all">
                          {redirectUri}
                        </code>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 px-3 flex-shrink-0"
                          onClick={() => copyToClipboard(redirectUri, 'help_redirect_uri')}
                        >
                          {copiedField === 'help_redirect_uri' ? (
                            <>
                              <Check className="h-3 w-3 text-green-500 mr-1" />
                              <span className="text-xs">Copied!</span>
                            </>
                          ) : (
                            <>
                              <Copy className="h-3 w-3 mr-1" />
                              <span className="text-xs">Copy</span>
                            </>
                          )}
                        </Button>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Step-by-Step Instructions */}
              <div className="space-y-3">
                <h3 className="font-semibold text-sm flex items-center gap-2">
                  <Info className="h-4 w-4" />
                  Step-by-Step Instructions
                </h3>
                <ol className="space-y-2">
                  {helpPlatform.setupInfo.steps?.map((step, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-semibold">
                        {idx + 1}
                      </span>
                      <span className="text-muted-foreground pt-0.5">
                        {step.replace('{baseUrl}', getBaseUrl())}
                      </span>
                    </li>
                  ))}
                </ol>
              </div>

              {/* Quick Links */}
              <div className="space-y-3">
                <h3 className="font-semibold text-sm flex items-center gap-2">
                  <ExternalLink className="h-4 w-4" />
                  Helpful Links
                </h3>
                <div className="flex flex-wrap gap-2">
                  {helpPlatform.setupInfo.consoleUrl && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(helpPlatform.setupInfo.consoleUrl, '_blank')}
                    >
                      <ExternalLink className="h-3 w-3 mr-2" />
                      Developer Console
                    </Button>
                  )}
                  {helpPlatform.setupInfo.adsUrl && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(helpPlatform.setupInfo.adsUrl, '_blank')}
                    >
                      <ExternalLink className="h-3 w-3 mr-2" />
                      Ads API Center
                    </Button>
                  )}
                  {helpPlatform.setupInfo.docsUrl && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(helpPlatform.setupInfo.docsUrl, '_blank')}
                    >
                      <ExternalLink className="h-3 w-3 mr-2" />
                      Official Documentation
                    </Button>
                  )}
                </div>
              </div>

              {/* Tips Section */}
              <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-xs">
                    <p className="font-medium text-amber-700 dark:text-amber-500">Important Tips</p>
                    <ul className="list-disc list-inside text-muted-foreground mt-1 space-y-1">
                      {helpPlatform.id === 'meta' && (
                        <>
                          <li>Make sure your Meta App is in &quot;Live&quot; mode for production use</li>
                          <li>Request the <code className="text-xs bg-muted px-1 rounded">ads_management</code> and <code className="text-xs bg-muted px-1 rounded">ads_read</code> permissions</li>
                          <li>The app must be associated with a Business Manager account</li>
                        </>
                      )}
                      {helpPlatform.id === 'google' && (
                        <>
                          <li>Developer tokens start in &quot;Test&quot; mode - apply for production access when ready</li>
                          <li>Enable the Google Ads API in your Google Cloud Console project</li>
                          <li>Use a Google account with admin access to your Google Ads accounts</li>
                        </>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="mt-4 border-t pt-4">
            <Button variant="outline" onClick={() => setHelpDialogOpen(false)}>
              Close
            </Button>
            <Button 
              onClick={() => {
                setHelpDialogOpen(false);
                openSetupInfo(helpPlatform?.id);
              }}
              data-testid="help-connect-btn"
            >
              <Link2 className="h-4 w-4 mr-2" />
              Connect {helpPlatform?.name}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ConnectionsModal;
